import React, { useState } from 'react';
import TaskRow from './TaskRow';
import { ProgressBar, accentStyle } from './TaskBits';
import {
	sortTasks,
	groupBy,
	areaMeta,
	AREA_ORDER,
	dueBucket,
} from './taskModel';
import { computeProjectProgress } from '../../../services/ProjectService';

// Portal Phase C — the list surface that backs four views (mode prop):
//   needs   → urgency buckets (Overdue → Today → This week → Later → No date →
//             Parked), Later + Parked collapsed by default; No date OPEN
//             (Anthony, 2026-07-25 — undated work is real work, and hiding it
//             made it invisible). Parked = any task carrying a `gate`.
//   area    → collapsible section per work-type
//   project → collapsible section per project, headed by its progress bar
//   today   → the focus cut: ONLY the Overdue + Today buckets
// Needs action and Today share one urgency-grouping path (renderUrgency); Today
// is the same path narrowed to the two live buckets. Children (parentId) render
// nested under their parent row, so grouping is done on roots only. Within each
// list/section, `sortBy` (the section-header sort control, WorkTasksModule) picks
// the order — client-side sort of the already-loaded array, no Firestore orderBy.

// Urgency section headers use the small-caps + hairline treatment from the mock,
// with the label tinted by bucket. `variant="urgency"` swaps in that header; the
// default variant is the serif section head used by By area / By project.
function Section({ title, accentArea, count, headerExtra, headerAction, defaultOpen = true, variant, tone, children }) {
	const [open, setOpen] = useState(defaultOpen);
	if (variant === 'urgency') {
		return (
			<div className="wt-urgency">
				<button
					type="button"
					className="wt-urgency__head"
					aria-expanded={open}
					onClick={() => setOpen((v) => !v)}>
					<i
						className={`fa-sharp fa-solid fa-chevron-right wt-group__chev${
							open ? ' is-open' : ''
						}`}
					/>
					<span
						className="wt-urgency__label"
						style={tone ? { color: tone } : undefined}>
						{title}
					</span>
					<span className="wt-urgency__count">{count}</span>
					<span className="wt-urgency__rule" />
				</button>
				{open && <div className="wt-list">{children}</div>}
			</div>
		);
	}
	return (
		<div style={accentArea ? accentStyle(accentArea) : undefined}>
			<div className="wt-group__headrow">
				<button
					type="button"
					className="wt-group__head"
					aria-expanded={open}
					onClick={() => setOpen((v) => !v)}>
					<i
						className={`fa-sharp fa-solid fa-chevron-right wt-group__chev${
							open ? ' is-open' : ''
						}`}
					/>
					{accentArea !== undefined && <span className="wt-group__dot" />}
					<span className="wt-group__name">{title}</span>
					<span className="wt-group__count">{count}</span>
					{headerExtra}
				</button>
				{headerAction}
			</div>
			{open && <div className="wt-list">{children}</div>}
		</div>
	);
}

// Parked sits last and starts COLLAPSED — it is the one bucket you are meant to
// skip past, but the count stays visible so parked work can't quietly vanish.
// (Today mode passes only overdue+today, so it never renders there.)
const TODAY_BUCKETS = [
	{ key: 'overdue', label: 'Overdue' },
	{ key: 'today', label: 'Today' },
	{ key: 'week', label: 'This week' },
	{ key: 'later', label: 'Later' },
	{ key: 'none', label: 'No date' },
	{ key: 'parked', label: 'Parked' },
];

// Bucket → label colour (mock urgency ramp). Overdue = danger, Today = warning,
// This week = body ink, Later / No date = muted. Every value is a live theme
// token, so the ramp recolors across Default / Comfort / Comfort-Dark.
const URGENCY_TONE = {
	overdue: 'var(--cyv-danger)',
	today: 'var(--cyv-warning)',
	week: 'var(--text-body)',
	later: 'var(--text-muted)',
	none: 'var(--text-muted)',
	parked: 'var(--text-muted)',
};

function TaskListView({
	mode,
	roots,
	childrenByParent,
	allTasks,
	projects,
	projectsById,
	now,
	sortBy,
	handlers,
	onOpenProject,
	emptyState,
	/* The area keys this SPACE offers, in display order (see areaOrderFor).
	   Defaults to the studio list so a direct mount or a test harness keeps the
	   behaviour it had before areas became per-space. Grouping reads this rather
	   than AREA_ORDER: a private area is not in the studio list, so "By area"
	   would have put every Personal task under one unlabelled group. */
	areaOrder = AREA_ORDER,
}) {
	const rowProps = (task) => ({
		task,
		children: childrenByParent.get(task.id) || [],
		projectsById,
		now,
		onOpen: handlers.onOpen,
		onAdvance: handlers.onAdvance,
		onAdvanceChild: handlers.onAdvance,
		// Set a due date from the row itself — the list is the "first state", and
		// this is what keeps a date change off the Edit-form path.
		onSetDeadline: handlers.onSetDeadline,
		// The phone quick-action bar. ONE handler serves all five actions because
		// every option carries its own `update` object (quickActions.js).
		onQuickUpdate: handlers.onQuickUpdate,
		fields: handlers.fields,
		offeredGates: handlers.offeredGates,
		areaOrder,
		// "changed since you last looked" — computed by the orchestrator, which
		// owns the read markers; undefined when the viewer has no marker yet.
		change: handlers.changeFor?.(task) || null,
		// area is the section in By-area — drop the redundant per-row area column
		hideAreaCol: mode === 'area',
		// The viewer's global show-completed preference, applied by the row to its
		// nested children (the row itself is already open-only by this point).
		showCompleted: handlers.showCompleted === true,
	});

	if (!roots.length) return emptyState;

	// Needs action + Today share this path. `bucketKeys` both orders the sections
	// and narrows them: Needs action passes all five buckets; Today passes only
	// the two live ones. groupBy can append keys outside `order`, so we filter the
	// result to bucketKeys — that's what drops week/later/none from Today. Empty
	// buckets never enter the map, so no empty headers render.
	const renderUrgency = (bucketKeys) => {
		const groups = groupBy(roots, (t) => dueBucket(t, now), bucketKeys).filter(
			(g) => bucketKeys.includes(g.key)
		);
		// Today can narrow to zero live buckets while later work still exists —
		// show the caught-up state rather than a blank surface. (Needs action always
		// has a bucket when roots is non-empty, so this only bites Today.)
		if (!groups.length) return emptyState;
		const labelFor = (k) => TODAY_BUCKETS.find((b) => b.key === k)?.label || k;
		return (
			<div className="wt-group">
				{groups.map((g) => (
					<Section
						key={g.key}
						variant="urgency"
						tone={URGENCY_TONE[g.key]}
						title={labelFor(g.key)}
						count={g.tasks.length}
						defaultOpen={g.key !== 'later' && g.key !== 'parked'}>
						{sortTasks(g.tasks, sortBy).map((t) => (
							<TaskRow key={t.id} {...rowProps(t)} />
						))}
					</Section>
				))}
			</div>
		);
	};

	if (mode === 'needs') {
		return renderUrgency(TODAY_BUCKETS.map((b) => b.key));
	}

	if (mode === 'area') {
		const groups = groupBy(
			roots,
			(t) => t.area || 'none',
			areaOrder,
			(k) => (k === 'none' ? 'No area' : areaMeta(k).label)
		);
		return (
			<div className="wt-group">
				{groups.map((g) => (
					<Section
						key={g.key}
						title={g.label}
						accentArea={g.key === 'none' ? null : g.key}
						count={g.tasks.length}>
						{sortTasks(g.tasks, sortBy).map((t) => (
							<TaskRow key={t.id} {...rowProps(t)} />
						))}
					</Section>
				))}
			</div>
		);
	}

	if (mode === 'project') {
		const groups = groupBy(
			roots,
			(t) => t.projectId || 'none',
			null,
			(k) => (k === 'none' ? 'No project' : projectsById[k]?.title || 'Project')
		);
		// Order: known projects alphabetically, "No project" last.
		groups.sort((a, b) => {
			if (a.key === 'none') return 1;
			if (b.key === 'none') return -1;
			return a.label.localeCompare(b.label);
		});
		return (
			<div className="wt-group">
				{groups.map((g) => {
					const proj = projectsById[g.key];
					const prog =
						g.key !== 'none'
							? computeProjectProgress(allTasks, g.key)
							: null;
					return (
						<Section
							key={g.key}
							title={g.label}
							accentArea={proj?.area || null}
							count={g.tasks.length}
							headerExtra={
								prog ? (
									<span className="wt-group__bar">
										<ProgressBar
											pct={prog.pct}
											accentArea={proj?.area}
										/>
									</span>
								) : null
							}
							headerAction={
								proj && onOpenProject ? (
									<button
										type="button"
										className="wt-iconbtn wt-group__open"
										aria-label={`Open ${proj.title}`}
										onClick={() => onOpenProject(proj)}>
										<i className="fa-sharp fa-solid fa-arrow-up-right-from-square" />
									</button>
								) : null
							}>
							{sortTasks(g.tasks, sortBy).map((t) => (
								<TaskRow key={t.id} {...rowProps(t)} />
							))}
						</Section>
					);
				})}
			</div>
		);
	}

	// mode === 'today' — the focus cut: only Overdue + Today.
	return renderUrgency(['overdue', 'today']);
}

export default TaskListView;
